// src/data/carriage.js
//
// CP-1 - THE CARRIAGE RECORD. Who is carrying this claim, how it got to them, and every move it has
// ever made. Authorised by Bob 2026-09-22 ("CP-1: yes by number" - **Yes**); plan at
// `data\YCP_PLAN_CLAIMANT_PATH_2026-09-21.md`, section "PLAN CP-1".
//
// WHY THIS IS A RECORD AND NOT A FLAG. Three rulings, and none of them survives a boolean:
//
//   135  Carriage transfers "upon submission by the claimant to her representative for their
//        furtherance of the claim itself to the carrier, unless otherwise specified by the
//        claimant." Three things live in that sentence: WHO acts (the claimant), WHAT PURPOSE
//        qualifies the act (furtherance to the carrier - not "have a look at this"), and HER
//        POWER TO SAY OTHERWISE. A boolean holds none of them.
//   143  It returns "if the participating parties agree, and the gc approval when the gc requires
//        it." A return therefore has signatories. A flag has nobody to name.
//   144  "The claimant self-represents until carriage moves, and can move it back." So the
//        DEFAULT HOLDER IS THE CLAIMANT, from the first write, with no act required to make it so.
//
// AND THE CONSEQUENCE BOB STATED IN HIS OWN WORDS (135, standing): "carriage never moves because an
// officer picked the matter up." That is enforced here as a validation, not as a convention -
// `validateAct` refuses a `submitted` act whose actor is not the claimant, so there is no code path
// in which an officer opening a claim can take it. See `officerMayMoveCarriage()`, which answers NO
// to everyone, always, and exists so that the answer is written down rather than assumed.
//
// WHAT THIS MODULE IS NOT. It is not editorial custody - ruling 72's return-to-draft and ruling 202's
// return-to-author point at `createdBy` and are a different field, deliberately (135: "Ownership
// (carriage) and editorial custody (27c) are two fields, not one"). It is not enforcement of the
// agreement, which is exclusively the General Chairperson's (136) and which no carriage act can move:
// an instruction reaching enforcement "is void, not an override."
//
// PURE. No Firestore, no network, no clock. The caller supplies the server time. Every function here
// is total: bad input yields a stated problem, never a throw.

// THE TWO HOLDERS. Not "organization" - a claim is carried by a PERSON who is named, because ruling
// 143's return needs parties who can agree and ruling 29 says the member can see who holds it.
export const CLAIMANT = 'claimant';
export const REPRESENTATIVE = 'representative';
export const HOLDERS = Object.freeze([CLAIMANT, REPRESENTATIVE]);

// THE ACTS. SUBMITTED (ruling 135). RULING 307 (Bob 2026-10-12) replaced ruling 143's agreed RETURN: once submitted the
// claim is the general committee's, and it comes back to the member in one of two ways only -
//   WITHDRAWN  the member withdraws HER submission - her act, alone, at any time (ruling 144 kept). It does not end the
//              union's claim: an officer may still file the same claim, for her or any member it affects (307 item 3).
//   RELEASED   the General Chairperson of the claim's committee releases the claim to her - hers alone (307 item 1).
// RETURNED stays readable so the acts already recorded under ruling 143 still project correctly; no new one is accepted.
export const SUBMITTED = 'submitted';
export const RETURNED = 'returned';
export const WITHDRAWN = 'withdrawn';
export const RELEASED = 'released';
export const ACTS = Object.freeze([SUBMITTED, RETURNED, WITHDRAWN, RELEASED]);
const BACK_TO_CLAIMANT = Object.freeze([RETURNED, WITHDRAWN, RELEASED]);
// RULING 307 item 3 with 307.1 ("her actions and the unions must be recorded accurtaely"): after she withdraws, the union may
// file the same claim again - for her or any member it affects. REFILED records that link on BOTH claims (the withdrawn one
// points to the new filing, the new filing points back). It moves no carriage, so it is not in ACTS and carriageOf never
// sees it; RECORD_ACTS is every act the carriage record accepts.
export const REFILED = 'refiled';
export const RECORD_ACTS = Object.freeze([...ACTS, REFILED]);

// RULING 307.1 (Bob: "the organization controls and is the only authority to interpret the controlling agreement, and
// enforcing its rules is that of the general chairperson"). Shown wherever the member acts for herself.
export const AUTHORITY_LINE = 'The organization alone interprets the controlling agreement, and enforcing its rules is '
  + 'the General Chairperson\'s.';
export const WITHDRAW_EXPLANATION = 'You are withdrawing your submission of this claim to the union. You may then pursue '
  + 'it yourself, including filing it with the carrier. The union may still pursue the same claim - for you or for any '
  + 'member it affects - because the agreement protects every member. ' + AUTHORITY_LINE + ' Your withdrawal is recorded.';
export const RELEASE_EXPLANATION = 'You are releasing this claim from the union back to the member. Only the General '
  + 'Chairperson may do this. The release is recorded with your name and the time.';

// RULING 135's QUALIFYING PURPOSE. The claimant says which of these she is doing, and only the first
// one is the threshold the ruling describes. The second is here precisely BECAUSE it is not a
// transfer: handing an officer a document to read is a real and common act, and a record that cannot
// express it will have carriage moved on it by mistake.
export const FURTHERANCE = 'furtherance_to_carrier';
export const FOR_REVIEW = 'for_review_only';
export const PURPOSES = Object.freeze([FURTHERANCE, FOR_REVIEW]);

// What the claimant is shown at the moment she decides. Her words, not the platform's opinion
// (ruling 204: no assessment of her claim, ever, in text a member reads here).
export const SUBMIT_EXPLANATION = Object.freeze({
  [FURTHERANCE]:
    'You are handing this claim to your representative to carry to the carrier. From this point your '
    + 'representative files it, meets the time limits and answers the carrier. The facts stay yours.',
  [FOR_REVIEW]:
    'You are asking your representative to look at this claim. You keep it. Nothing about who carries '
    + 'it changes, and you may hand it over later.',
});

export function purposeExplanation(purpose) {
  return SUBMIT_EXPLANATION[String(purpose || '')] || '';
}

// ---------------------------------------------------------------------------------------------
// VALIDATION
// ---------------------------------------------------------------------------------------------

// An act is dated, attributed and purposive, or it is not an act. `claimantUid` is passed in by the
// caller from the CLAIM, never from the act itself - an act that carried its own idea of who the
// claimant is could assert its way past the one rule this module exists to enforce.
export function validateAct(act, { claimantUid, currentHolder, currentHolderUid } = {}) {
  const problems = [];
  const a = act || {};
  const kind = String(a.act || '');
  const actorUid = String(a.actorUid || '').trim();
  const claimant = String(claimantUid || '').trim();

  if (!ACTS.includes(kind)) problems.push('act must be one of: ' + ACTS.join(', '));
  if (!actorUid) problems.push('an act must name who performed it');
  if (!String(a.actorEmail || '').trim()) problems.push('an act must carry the address it was performed from');
  if (!String(a.at || '').trim()) problems.push('an act must be dated');
  if (!claimant) problems.push('the claim must name its claimant before carriage can move');

  if (kind === SUBMITTED) {
    // RULING 135, THE WHOLE POINT. The claimant's act, or no act at all.
    if (claimant && actorUid && actorUid !== claimant) {
      problems.push('only the claimant may submit her own claim - carriage never moves because an '
        + 'officer picked the matter up');
    }
    if (!PURPOSES.includes(String(a.purpose || ''))) {
      problems.push('a submission must state its purpose: ' + PURPOSES.join(' or '));
    }
    if (!String(a.toUid || '').trim()) problems.push('a submission must name the representative it goes to');
    if (currentHolder === REPRESENTATIVE && String(a.purpose) === FURTHERANCE
        && String(a.toUid || '').trim() === String(currentHolderUid || '').trim()) {
      problems.push('this claim is already carried by that representative');
    }
  }

  if (kind === WITHDRAWN) {
    // RULING 307 item 2: the member's own act, and nobody else's.
    if (claimant && actorUid && actorUid !== claimant) problems.push('only the claimant may withdraw her own submission');
    if (currentHolder !== REPRESENTATIVE) problems.push('this claim is not with the union - there is no submission to withdraw');
  }

  if (kind === RELEASED) {
    // RULING 307 item 1: the General Chairperson's act, and nobody else's. The caller has already decided she is the claim's
    // General Chairperson (isClaimGc); the act records the role and committee she acted in.
    if (String(a.actorRole || '') !== 'general_chair') problems.push('only the General Chairperson may release a claim from the union');
    if (!String(a.committee || '').trim()) problems.push('a release must name the committee whose General Chairperson released it');
    if (currentHolder !== REPRESENTATIVE) problems.push('this claim is not with the union - there is nothing to release');
  }

  if (kind === RETURNED) {
    // RULING 143 (replaced by ruling 307 for new acts - mayAct refuses one). A return is an AGREEMENT, so it names who agreed. The claimant is a participating
    // party in every case, and so is whoever is holding it - those two are the floor, not the ceiling.
    const agreed = Array.isArray(a.agreedBy) ? a.agreedBy.filter((p) => p && String(p.uid || '').trim()) : [];
    const uids = agreed.map((p) => String(p.uid).trim());
    if (claimant && !uids.includes(claimant)) problems.push('a return requires the claimant\'s agreement');
    const holderUid = String(currentHolderUid || '').trim();
    if (holderUid && !uids.includes(holderUid)) {
      problems.push('a return requires the agreement of the representative currently carrying it');
    }
    if (currentHolder === CLAIMANT) problems.push('this claim is already carried by the claimant');
    // "plus the General Chairperson's approval WHERE THE GENERAL CHAIRPERSON REQUIRES IT" - so the
    // record carries both halves: whether it was required, and if so who gave it. A return that
    // says approval was required and names nobody is not a return.
    if (a.gcApprovalRequired === true && !String(a.gcApprovedByUid || '').trim()) {
      problems.push('the General Chairperson requires approval of this return and none is recorded');
    }
    if (a.gcApprovalRequired !== true && a.gcApprovalRequired !== false) {
      problems.push('a return must state whether the General Chairperson required approval');
    }
  }

  return { ok: problems.length === 0, problems };
}

// ---------------------------------------------------------------------------------------------
// THE RECORD
// ---------------------------------------------------------------------------------------------

// Append-only by construction. Moving carriage back is a NEW act, never an edit of the old one, for
// the same reason a §44 notice is never edited: the question the record answers is what was done,
// and when, and by whom - and an edited act cannot answer it.
export function buildAct(input) {
  const a = input || {};
  const kind = String(a.act || '');
  const base = {
    claimId: String(a.claimId || ''),
    act: kind,
    actorUid: String(a.actorUid || ''),
    actorEmail: String(a.actorEmail || ''),
    actorName: String(a.actorName || ''),
    at: String(a.at || ''),
    note: String(a.note || ''),
  };
  if (kind === SUBMITTED) {
    return Object.assign(base, {
      purpose: String(a.purpose || ''),
      toUid: String(a.toUid || ''),
      toName: String(a.toName || ''),
      toRole: String(a.toRole || ''),
      // Ruling 135's "unless otherwise specified by the claimant", recorded as HER words where she
      // gave any. A specification the record cannot show is a specification nobody can honour.
      specified: String(a.specified || ''),
    });
  }
  if (kind === RELEASED) {
    return Object.assign(base, { actorRole: String(a.actorRole || ''), committee: String(a.committee || '') });
  }
  if (kind === REFILED) {
    // One of the two directions is filled: toClaim* on the withdrawn claim, fromClaim* on the union's new filing.
    return Object.assign(base, {
      actorRole: String(a.actorRole || ''), committee: String(a.committee || ''),
      toClaimId: String(a.toClaimId || ''), toClaimNumber: String(a.toClaimNumber || ''),
      fromClaimId: String(a.fromClaimId || ''), fromClaimNumber: String(a.fromClaimNumber || ''),
    });
  }
  if (kind === RETURNED) {
    return Object.assign(base, {
      agreedBy: (Array.isArray(a.agreedBy) ? a.agreedBy : []).map((p) => ({
        uid: String((p && p.uid) || ''),
        name: String((p && p.name) || ''),
        role: String((p && p.role) || ''),
        at: String((p && p.at) || ''),
      })),
      gcApprovalRequired: a.gcApprovalRequired === true,
      gcApprovedByUid: String(a.gcApprovedByUid || ''),
      gcApprovedByName: String(a.gcApprovedByName || ''),
    });
  }
  return base;
}

// ---------------------------------------------------------------------------------------------
// WHO HOLDS IT
// ---------------------------------------------------------------------------------------------

// Computed from the acts and from nothing else, so a stored `carriedBy` can never disagree with the
// history that produced it. RULING 144 IS THE FIRST LINE OF THIS FUNCTION: with no acts at all, the
// claimant holds her own claim. There is no "unassigned" and no "unrepresented" - those were the
// states this record was built to abolish.
export function carriageOf(acts, { claimantUid, claimantName } = {}) {
  const list = (Array.isArray(acts) ? acts : []).filter((a) => a && ACTS.includes(String(a.act)));
  const atClaimant = {
    holder: CLAIMANT,
    holderUid: String(claimantUid || ''),
    holderName: String(claimantName || ''),
    since: '',
    act: null,
    moves: 0,
  };
  if (!list.length) return atClaimant;

  // Order of record. A server clock makes ties practically impossible; arrival order resolves them
  // anyway so the function is total rather than merely usually-correct.
  const sorted = list.slice().sort((a, b) => String(a.at).localeCompare(String(b.at)));
  let state = atClaimant;
  let moves = 0;
  for (const a of sorted) {
    if (String(a.act) === SUBMITTED) {
      // FOR_REVIEW is recorded and changes nothing. That is the whole reason the purpose exists.
      if (String(a.purpose) !== FURTHERANCE) continue;
      state = {
        holder: REPRESENTATIVE,
        holderUid: String(a.toUid || ''),
        holderName: String(a.toName || ''),
        since: String(a.at || ''),
        act: a,
        moves: ++moves,
      };
    } else if (BACK_TO_CLAIMANT.includes(String(a.act))) {
      state = {
        holder: CLAIMANT,
        holderUid: String(claimantUid || ''),
        holderName: String(claimantName || ''),
        since: String(a.at || ''),
        act: a,
        moves: ++moves,
      };
    }
  }
  return state;
}

// Ruling 29: the member can see who holds it, in words, without being asked to read a field name.
export function carriageLine(state, { claimantName } = {}) {
  const s = state || {};
  if (s.holder === REPRESENTATIVE) {
    const who = String(s.holderName || '').trim() || 'your representative';
    return 'This claim is being carried by ' + who + (s.since ? ', since ' + s.since : '') + '.';
  }
  const me = String(claimantName || s.holderName || '').trim();
  const how = s.act && String(s.act.act) === WITHDRAWN ? ' The submission was withdrawn'
    : s.act && String(s.act.act) === RELEASED ? ' The General Chairperson released it'
    : ' It was returned';
  return (me ? me + ' carries this claim.' : 'You carry this claim.')
    + (s.moves ? how + (s.since ? ' on ' + s.since : '') + '.' : '');
}

// ---------------------------------------------------------------------------------------------
// WHAT AN OFFICER MAY DO - which is, as to carriage, NOTHING
// ---------------------------------------------------------------------------------------------

// Written as a function returning a constant because the alternative is an assumption living in
// whoever reads the rules next. Ruling 135: "carriage never moves because an officer picked the
// matter up." No role, no seniority and no emergency changes this answer; a representative who needs
// the claim asks the claimant to submit it, and the claimant's act is the record.
export function officerMayMoveCarriage() {
  return {
    may: false,
    because: 'Carriage moves on the claimant\'s act, never the office\'s. Ask the '
      + 'claimant to submit the claim for furtherance; her submission is what moves it.',
  };
}

// Whether THIS person may perform THIS act - the one question every caller actually has.
export function mayAct(kind, { actorUid, claimantUid, state, actor: actorObj, committeeId } = {}) {
  const actor = String(actorUid || (actorObj && actorObj.uid) || '').trim();
  const claimant = String(claimantUid || '').trim();
  const s = state || {};
  if (String(kind) === SUBMITTED) {
    if (!actor || actor !== claimant) return officerMayMoveCarriage();
    return { may: true, because: '' };
  }
  if (String(kind) === WITHDRAWN) {
    if (!actor || actor !== claimant) return { may: false, because: 'Only the member may withdraw her own submission.' };
    if (s.holder !== REPRESENTATIVE) return { may: false, because: 'This claim is not with the union.' };
    return { may: true, because: '' };
  }
  if (String(kind) === RELEASED) {
    if (!isClaimGc(actorObj, committeeId)) return { may: false, because: 'Only the General Chairperson of this claim\'s committee may release it from the union.' };
    if (s.holder !== REPRESENTATIVE) return { may: false, because: 'This claim is not with the union.' };
    return { may: true, because: '' };
  }
  if (String(kind) === RETURNED) {
    return { may: false, because: 'Ruling 307 replaced the agreed return: the member withdraws her submission, or the '
      + 'General Chairperson releases the claim.' };
  }
  return { may: false, because: 'unknown act' };
}

// The fields a claim carries. Named here so the schema, the server and the rules cannot drift apart:
// every one of them is derived, and none of them is writable by a client.
export const CLAIM_FIELDS = Object.freeze(['carriedBy', 'carriedByUid', 'carriedByName', 'carriageSince', 'carriageLastAct']);

// The projection written onto the claim after any act, so a desk query can filter without reading
// every act. It is a CACHE of `carriageOf` and nothing else - which is why the suite proves the two
// agree rather than trusting that they do.
export function claimProjection(state) {
  const s = state || {};
  return {
    carriedBy: String(s.holder || CLAIMANT),
    carriedByUid: String(s.holderUid || ''),
    carriedByName: String(s.holderName || ''),
    carriageSince: String(s.since || ''),
    // Master list item 6 (Bob 2026-10-14): WHICH act put the claim where it is - 'withdrawn' tells a desk the member
    // pulled her submission (ruling 307), where carriedBy alone reads the same for withdrawn, released and never sent.
    carriageLastAct: String((s.act && s.act.act) || ''),
  };
}

// ---------------------------------------------------------------------------------------------
// WHO IS THE CLAIM'S GENERAL CHAIRPERSON - Record 641, narrowed by ruling 307 item 4
// ---------------------------------------------------------------------------------------------

// The bubbles review of 2026-10-11 (Record 632.2 (a)) found that a General Chairperson had no on-screen way to approve a
// return where her committee requires it, so such a return could never complete. Behind the screen the server named her
// as committees/{c}.generalChairUid - a field nothing in the platform writes. She is now the account the platform already
// recognises as that committee's General Chairperson: role general_chair and committee equal to the claim's committee
// (token claims written only by set-roles.js and setOfficeHolder). RULING 307 item 4 (Bob 2026-10-12): that is the ONE way -
// the recorded generalChairUid fallback is dropped (nothing wrote it; ORPH-1, Record 658). One function decides, for the
// server (carriageAct) and the screen (CarriagePanel) alike.
export const GC_SLOT = 'general_chair';

export function isClaimGc(actor, committeeId) {
  const a = actor || {};
  const uid = String(a.uid || '').trim();
  const c = String(committeeId || '').trim();
  if (!uid) return false;
  return String(a.role || '') === 'general_chair' && !!c && String(a.committee || '') === c;
}

// ---------------------------------------------------------------------------------------------
// THE UNION FILES THE SAME CLAIM AGAIN - ruling 307 item 3, recorded per 307.1
// ---------------------------------------------------------------------------------------------

// An officer of the claim: its committee's General Chairperson, or a Local Chairperson of that committee whose token scopes
// carry the claim's territory (the same token claims every other callable reads).
export function isClaimOfficer(actor, committeeId, scopeKey) {
  if (isClaimGc(actor, committeeId)) return true;
  const a = actor || {};
  const c = String(committeeId || '').trim(), s = String(scopeKey || '').trim();
  return !!String(a.uid || '').trim() && String(a.role || '') === 'local_chair' && !!c && String(a.committee || '') === c
    && !!s && Array.isArray(a.scopes) && a.scopes.includes(s);
}

// May this officer record that the union filed `newClaim` as the same claim as the withdrawn `claim`? Both claims are read
// by the caller from the record, never from the request. Returns { may, because }.
export function mayRecordRefiling({ actor, claim, claimId, state, newClaim, newClaimId, alreadyLinked } = {}) {
  const c = claim || {}, n = newClaim || {}, s = state || {};
  const committeeId = String(c.committee || '').trim();
  if (!isClaimOfficer(actor, committeeId, c.scopeKey)) {
    return { may: false, because: 'Only a Local Chairperson of this claim\'s territory or the General Chairperson of its committee records the union\'s filing.' };
  }
  if (!(s.act && String(s.act.act) === WITHDRAWN)) {
    return { may: false, because: 'The union\'s filing is linked here after the member has withdrawn her submission.' };
  }
  if (!String(newClaimId || '').trim() || String(newClaimId) === String(claimId)) return { may: false, because: 'Name the union\'s new claim - a different claim from this one.' };
  if (!newClaim) return { may: false, because: 'No such claim.' };
  if (String(n.committee || '').trim() !== committeeId) return { may: false, because: 'The union\'s claim must be of the same committee.' };
  if (!isClaimOfficer(actor, committeeId, n.scopeKey)) return { may: false, because: 'The union\'s claim must be one you are an officer of.' };
  if (String(n.status || '') === 'draft') return { may: false, because: 'File the union\'s claim first; a draft is not a filing.' };
  if (alreadyLinked) return { may: false, because: 'That claim is already recorded as a filing of this one.' };
  return { may: true, because: '' };
}

// The line each claim shows for a REFILED act.
export function refiledLine(a) {
  const x = a || {};
  if (String(x.toClaimId || '')) return 'The union filed this claim again, as claim ' + (x.toClaimNumber || x.toClaimId) + '.';
  if (String(x.fromClaimId || '')) return 'The union filed this claim again from claim ' + (x.fromClaimNumber || x.fromClaimId) + ', whose submission the member withdrew.';
  return '';
}
